import React, { createContext, ReactNode, useState, useContext, useEffect } from "react";
import { GoogleMapsClient } from "src/lib/maps_client";
import { Libraries, useLoadScript } from "@react-google-maps/api";

interface MapsContextValue {
  mapsClient: GoogleMapsClient | null;
  isGoogleMapsLoaded: boolean;
  googleMapsLoadError: Error | undefined;
}

const MapsContext = createContext<MapsContextValue>({
  mapsClient: null,
  isGoogleMapsLoaded: false,
  googleMapsLoadError: undefined,
});

const MAP_LIBRARIES: Libraries = ['places', 'geometry'];

export const MapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mapsClient, setMapsClient] = useState<GoogleMapsClient | null>(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: MAP_LIBRARIES
  });

  useEffect(() => {
    if (isLoaded && window.google && window.google.maps) {
      const client = new GoogleMapsClient();
      client.initialize();
      setMapsClient(client);
    }
  }, [isLoaded]);

  const contextValue = {
    mapsClient,
    isGoogleMapsLoaded: isLoaded,
    googleMapsLoadError: loadError
  };

  return (
    <MapsContext.Provider value={contextValue}>
      {children}
    </MapsContext.Provider>
  );
};

export const useMaps = (): MapsContextValue => {
  const context = useContext(MapsContext);
  if (context === undefined) {
    throw new Error('useMaps must be used within a MapsProvider');
  }
  return context;
};
